import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { NavigationItem, SidebarProps } from '@/types/components';
import { APP_CONFIG } from '@/config/app';
import { canAccessShiftDashboard } from '@/utils/access';

const BASE_NAVIGATION_ITEMS: NavigationItem[] = [
  {
    id: 'attendance',
    title: 'Attendance',
    router: '/attendance',
    icon: 'ri-calendar-check-line',
  },
];

const bottomMenuItems: NavigationItem[] = [
  {
    id: 'settings',
    title: 'Settings',
    router: '/settings',
    icon: 'ri-settings-4-line',
  },
  {
    id: 'support',
    title: 'Support',
    router: '/support',
    icon: 'ri-question-line',
  },
];

const renderMenuItem = (
  item: NavigationItem,
  isActive: boolean,
  isCollapsed: boolean,
  extraClasses: string = ''
) => {
  const baseClasses = isCollapsed
    ? `w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${extraClasses}`
    : `group flex items-center px-3 py-2 text-sm font-sans font-medium rounded-lg transition-colors ${extraClasses}`;

  const activeClasses = isActive
    ? isCollapsed
      ? 'bg-purple-100 text-primary'
      : 'bg-purple-50 text-primary border-r-2 border-primary'
    : isCollapsed
      ? 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
      : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900';

  const disabledClasses = item.disabled
    ? 'opacity-50 cursor-not-allowed pointer-events-none'
    : '';

  return (
    <Link
      key={item.id}
      to={item.disabled ? '#' : item.router}
      className={`${baseClasses} ${activeClasses} ${disabledClasses}`.trim()}
      title={isCollapsed ? item.title : undefined}
      onClick={(e) => item.disabled && e.preventDefault()}
    >
      <i
        className={`${item.icon} text-[20px] ${
          isCollapsed
            ? ''
            : isActive
              ? 'text-primary'
              : 'text-gray-400 group-hover:text-gray-500'
        }`}
      ></i>
      {!isCollapsed && (
        <>
          <span className="ml-3">{item.title}</span>
          {item.badge && (
            <span className="ml-auto mr-2 px-2 py-0.5 text-xs font-medium bg-purple-100 text-primary rounded-full">
              {item.badge}
            </span>
          )}
          <i className="ri-arrow-right-s-line text-[16px] ml-auto"></i>
        </>
      )}
      {isCollapsed && item.badge && (
        <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-white text-xs rounded-full flex items-center justify-center">
          {typeof item.badge === 'number' && item.badge > 9 ? '9+' : item.badge}
        </span>
      )}
    </Link>
  );
};

export const Sidebar: React.FC<SidebarProps> = ({
  isCollapsed,
  onToggleCollapse,
  currentPath = APP_CONFIG.homeRoute,
}) => {
  const { user, signOut } = useAuth();
  // Dashboard sits on top, for super admins only (Dash's and the client's) —
  // see canAccessShiftDashboard.
  const navigationItems: NavigationItem[] =
    canAccessShiftDashboard(user)
      ? [
          {
            id: 'shift-dashboard',
            title: 'Dashboard',
            router: '/shift-dashboard',
            icon: 'ri-dashboard-line',
          },
          ...BASE_NAVIGATION_ITEMS,
        ]
      : BASE_NAVIGATION_ITEMS;

  const handleLogout = async () => {
    try {
      await signOut();
    } catch (error) {}
  };

  return (
    <div
      className={`bg-white border-r border-gray-200 flex flex-col h-full transition-all duration-300 ${
        isCollapsed ? 'w-16' : 'w-64'
      }`}
    >
      <div
        className={`flex items-center ${
          isCollapsed ? 'justify-center p-4' : 'justify-between p-4'
        } border-b border-gray-200`}
      >
        <div
          className={`flex ${
            isCollapsed
              ? 'flex-col items-center space-y-2'
              : 'flex-row items-center space-x-3'
          }`}
        >
          <div className="w-8 h-8 rounded-lg flex items-center justify-center">
            <img
              src="/images/dash-electric.png"
              alt={`${APP_CONFIG.shortName} logo`}
              className="w-6 h-6"
            />
          </div>
          {isCollapsed && (
            <button
              onClick={onToggleCollapse}
              className="hidden sm:flex p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <i className="ri-sidebar-unfold-line text-[20px] text-gray-600"></i>
            </button>
          )}
          {!isCollapsed && (
            <div>
              <h1 className="text-lg font-sans font-semibold text-gray-900">
                {APP_CONFIG.name}
              </h1>
              <p className="text-xs font-sans text-gray-500">
                {APP_CONFIG.subtitle}
              </p>
            </div>
          )}
        </div>
        {!isCollapsed && (
          <button
            onClick={onToggleCollapse}
            className="hidden sm:flex p-1 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <i className="ri-sidebar-fold-line text-[20px] text-gray-600"></i>
          </button>
        )}
      </div>

      <nav
        className={`flex-1 flex flex-col ${
          isCollapsed ? 'items-center space-y-6 py-4' : 'px-3 py-4'
        }`}
      >
        {isCollapsed ? (
          <>
            {navigationItems.map((item) => {
              const isActive = currentPath === item.router;
              return (
                <div key={item.id} className="relative">
                  {renderMenuItem(item, isActive, true)}
                </div>
              );
            })}
          </>
        ) : (
          <div className="space-y-1">
            <div className="text-xs font-sans font-medium text-gray-500 uppercase tracking-wide mb-3">
              Main
            </div>

            {navigationItems.map((item) => {
              const isActive = currentPath === item.router;
              return renderMenuItem(item, isActive, false);
            })}
          </div>
        )}
      </nav>

      {isCollapsed ? (
        <div className="p-4 flex flex-col items-center space-y-4">
          {bottomMenuItems.map((item) => {
            const isActive = currentPath === item.router;
            return (
              <div key={item.id} className="relative">
                {renderMenuItem(item, isActive, true)}
              </div>
            );
          })}

          {user && (
            <div className="relative group mt-4">
              <img
                src={
                  user.photoURL ||
                  `https://ui-avatars.com/api/?name=${encodeURIComponent(
                    user.displayName || user.email || 'User'
                  )}&background=6366f1&color=fff`
                }
                alt={user.displayName || user.email || 'User'}
                className="w-8 h-8 rounded-full cursor-pointer"
                title={user.displayName || user.email || 'User'}
              />

              <button
                onClick={handleLogout}
                className="absolute -top-1 -right-1 w-4 h-4 bg-white border border-gray-200 rounded-full flex items-center justify-center hover:bg-gray-50 transition-colors"
                title="Expand sidebar"
              >
                <i className="ri-arrow-right-s-line text-[8px]"></i>
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="border-t border-gray-200 p-3">
          {bottomMenuItems.map((item) => {
            const isActive = currentPath === item.router;
            return renderMenuItem(item, isActive, false, 'mb-2');
          })}

          {user && (
            <div className="flex items-center space-x-3 p-3 rounded-lg bg-gray-50 mt-1">
              <img
                src={
                  user.photoURL ||
                  `https://ui-avatars.com/api/?name=${encodeURIComponent(
                    user.displayName || user.email || 'User'
                  )}&background=6366f1&color=fff`
                }
                alt={user.displayName || user.email || 'User'}
                className="w-8 h-8 rounded-full"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-sans font-medium text-gray-900 truncate">
                  {user.displayName || user.name || 'User'}
                </p>
                <p className="text-xs font-sans text-gray-500 truncate">
                  {user.email}
                </p>
              </div>
              <button
                onClick={handleLogout}
                className="p-1 rounded-lg hover:bg-red-100 hover:text-red-600 transition-colors group"
                title="Logout"
              >
                <i className="ri-logout-box-line text-[16px] text-gray-400 group-hover:text-red-600"></i>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
